import React, {useState, useMemo} from 'react';
import { CustomHeroButton, CustomHeroInput, LogoutButton } from '../../components';
import Typography from '../../components/typography/Typography';
import { Card, CardBody } from '@heroui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import { authService } from '../../api';
import { calculatePasswordStrength, getPasswordStrengthColor } from '../../utils/password';
import { Icon } from '@iconify/react/dist/iconify.js';
import { showPartnerSuccess, showError } from '../../utils';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

// Validation schema for profile (without avatar)
const createProfileSchema = (t: any) => {
  return Yup.object().shape({
    name: Yup.string()
      .min(2, t('settings.minSymbols'))
      .required(t('settings.required')),
  });
};

// Validation schema for password change
const changePasswordSchema = (t: any) => Yup.object({
  password: Yup.string()
    .min(4, t('settings.passwordRequirements'))
    .max(25, t('settings.passwordRequirements'))
    .matches(/[A-Z]/, t('settings.passwordRequirements'))
    .matches(/[0-9]/, t('settings.passwordRequirements'))
    .required(t('settings.passwordRequired')),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], t('settings.passwordsMatch'))
    .required(t('settings.confirmPasswordRequired')),
});

const Settings: React.FC = () => {
  const {user, refreshUser} = useAuth()
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.settings'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);

  const handleSubmit = async (values: any) => {
    setIsUpdating(true);
    try {
      // Check if there are profile changes
      const hasProfileChanges =
        values.name !== user?.name;

      // Update profile data only if there are changes
      if (hasProfileChanges) {
        // Backend requires both name and password in the request body
        // Send empty string for password when only updating name
        await authService.updateProfile({
          name: values.name,
          password: '', // Empty password means no password change
        });
      }

      // Only refresh user if there were actual changes
      if (hasProfileChanges) {
        await refreshUser();
        setError(null); // Clear profile error on success
        showPartnerSuccess(t('settings.updateSuccess'));
      }

    } catch (error: any) {
      console.error('Error updating credentials:', error);
      setError(error?.response?.data?.detail || t('settings.updateError'));
      showError(error?.response?.data?.detail || t('settings.updateError'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubmitPassword = async (values: any, actions: any) => {
    setIsUpdatingPassword(true);
    try {
      // Backend requires name field, so we send current user name along with password
      await authService.changePasswordSettings({
        password: values?.password,
        name: user?.name || ''
      });
      showPartnerSuccess(t('settings.updatePasswordSuccess'));
      await refreshUser();
      // Reset form after successful password change
      actions.resetForm();
    } catch (error: any) {
      showError(t('settings.updatePasswordError'));
      setError(error?.response?.data?.detail || t('settings.updateError'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

  return (
    <div className="flex flex-col gap-[16px] px-[24px]">
      <Typography className='py-[16px]' variant='h5'>{t('settings.accountSettings')}</Typography>

      <div className='flex flex-col gap-[24px] w-[530px]'>
        {/* Profile Information Form */}
        <Formik
          initialValues={{
            email: user?.email || '',
            name: user?.name || '',
          }}
          validationSchema={createProfileSchema(t)}
          onSubmit={(values, actions) => {
            handleSubmit(values);
            actions.setSubmitting(false);
          }}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid }) => {
            const hasProfileChanges = values.name !== user?.name;
            const canSubmit = hasProfileChanges && !isUpdating;

            return (
              <Form onSubmit={handleSubmit}>
                <div className='flex flex-col gap-[16px] items-start'>
                  <Typography variant='h6'>{user?.name}</Typography>
                  <Card className="border w-[530px] border-[#e5e5e5] shadow-sm rounded-[14px]">
                    <CardBody className="p-[16px]">
                      <div className="flex flex-col gap-[16px]">
                        <CustomHeroInput
                          label={t('settings.email')}
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.email}
                          touched={touched.email}
                          disabled={true}
                        />
                        <CustomHeroInput
                          label={t('settings.name')}
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          isRequired
                          onBlur={handleBlur}
                          error={errors.name}
                          touched={touched.name}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  <CustomHeroButton
                    className='mt-[8px]'
                    disabled={!canSubmit}
                    isLoading={isUpdating}
                    title={isUpdating ? t('settings.updating') : t('settings.saveChanges')}
                    onPress={() => handleSubmit()}
                  />
                </div>
              </Form>
            );
          }}
        </Formik>

        {/* Change Password Form */}
        <Formik
          initialValues={{
            password: '',
            confirmPassword: '',
          }}
          validationSchema={changePasswordSchema(t)}
          onSubmit={(values, actions) => {
            handleSubmitPassword(values, actions);
            actions.setSubmitting(false);
          }}
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue, handleBlur, handleSubmit, isValid }) => (
            <Form onSubmit={handleSubmit}>
              <div className='flex flex-col items-start gap-[16px]'>
                <Typography variant='h6'>{t('settings.changePassword')}</Typography>
                <Card className="border w-[530px] border-[#e5e5e5] shadow-sm rounded-[14px]">
                  <CardBody className="p-[16px]">
                    <div className="flex flex-col gap-[16px]">
                      {/* New Password Field */}
                      <div className="flex flex-col w-full">
                        <CustomHeroInput
                          label={t('settings.password')}
                          endContent={
                            <button type="button" onClick={toggleVisibility}>
                              {isVisible ? (
                                <Icon
                                  className="pointer-events-none text-2xl w-[20px] text-default-400"
                                  icon="solar:eye-closed-linear"
                                />
                              ) : (
                                <Icon
                                  className="pointer-events-none text-2xl w-[20px] text-default-400"
                                  icon="solar:eye-bold"
                                />
                              )}
                            </button>
                          }
                          name="password"
                          placeholder={t('settings.enterPassword')}
                          type={isVisible ? "text" : "password"}
                          value={values.password}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("password", e.target.value)}
                          error={errors.password}
                          touched={touched.password}
                        />

                        {/* Password Strength Indicator - 4 bars */}
                        <div className="flex gap-[4px] w-full mt-[8px]">
                          {[1, 2, 3, 4].map((level) => {
                            const currentStrength = calculatePasswordStrength(values.password);
                            const isActive = currentStrength >= level;
                            const strengthColor = getPasswordStrengthColor(currentStrength);
                            const color = isActive ? strengthColor : '#d4d4d8';

                            return (
                              <div
                                key={level}
                                className="h-[6px] flex-1 rounded-[14px] password-strength-bar"
                                style={{ backgroundColor: color }}
                              />
                            );
                          })}
                        </div>

                        {/* Password Requirements Hint */}
                        <p className="text-[14px] text-[#71717a] leading-[20px] mt-[4px]">
                          {t('settings.passwordHint')}
                        </p>
                      </div>

                      {/* Confirm Password Field */}
                      <CustomHeroInput
                        label={t('settings.repeatPassword')}
                        endContent={
                          <button type="button" onClick={toggleConfirmVisibility}>
                            {isConfirmVisible ? (
                              <Icon
                                className="pointer-events-none text-2xl w-[20px] text-default-400"
                                icon="solar:eye-closed-linear"
                              />
                            ) : (
                              <Icon
                                className="pointer-events-none text-2xl w-[20px] text-default-400"
                                icon="solar:eye-bold"
                              />
                            )}
                          </button>
                        }
                        name="confirmPassword"
                        placeholder={t('settings.enterPasswordAgain')}
                        type={isConfirmVisible ? "text" : "password"}
                        value={values.confirmPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("confirmPassword", e.target.value)}
                        error={errors.confirmPassword}
                        touched={touched.confirmPassword}
                      />
                    </div>
                  </CardBody>
                </Card>
                <CustomHeroButton
                  className='mt-[8px]'
                  disabled={isUpdatingPassword || !isValid}
                  isLoading={isUpdatingPassword}
                  title={isUpdatingPassword ? t('settings.updating') : t('settings.savePassword')}
                  onPress={() => handleSubmit()}
                />
              </div>
            </Form>
          )}
        </Formik>

        {/* Logout Button Section */}
        <div className='flex flex-col gap-[16px] mt-[24px]'>
          <LogoutButton
            variant="bordered"
            size="lg"
            fullWidth
            className="w-[530px]"
          />
        </div>
      </div>
    </div>
  );
};

export default Settings;
